import { MRT_ColumnDef, MRT_RowData, MRT_Updater, MRT_VisibilityState } from 'material-react-table'
import { useCallback, useEffect, useRef, useState } from 'react'

export type TableColumnPreferencesData = {
  columnVisibility?: Record<string, boolean>
  columnOrder?: string[]
}

export type ColumnPreferencesConfig<TData extends MRT_RowData> = {
  columns: MRT_ColumnDef<TData>[]
  savedPreferences: TableColumnPreferencesData | undefined
  onPreferencesChange: (prefs: TableColumnPreferencesData) => void
}

type ColumnPreferencesResult = {
  columnVisibility: MRT_VisibilityState
  columnOrder: string[]
  onColumnVisibilityChange: (updater: MRT_Updater<MRT_VisibilityState>) => void
  onColumnOrderChange: (updater: MRT_Updater<string[]>) => void
}

const MRT_INTERNAL_COLUMNS = ['mrt-row-actions', 'mrt-row-expand', 'mrt-row-select']

function getColumnIds<TData extends MRT_RowData>(
  columns: MRT_ColumnDef<TData>[],
): string[] {
  return columns
    .map(c => (c.accessorKey as string) ?? c.id)
    .filter(Boolean)
}

function mergeColumnOrder<TData extends MRT_RowData>(
  savedOrder: string[] | undefined,
  columns: MRT_ColumnDef<TData>[],
): string[] {
  const dataColumnIds = getColumnIds(columns)
  const allColumnIds = new Set([...MRT_INTERNAL_COLUMNS, ...dataColumnIds])

  if (!savedOrder || savedOrder.length === 0) {
    return [...MRT_INTERNAL_COLUMNS, ...dataColumnIds]
  }

  const validSaved = savedOrder.filter(id => allColumnIds.has(id))
  const savedSet = new Set(validSaved)
  const internalSet = new Set(MRT_INTERNAL_COLUMNS)
  const newInternalColumns = MRT_INTERNAL_COLUMNS.filter(id => !savedSet.has(id))
  const newDataColumns = dataColumnIds.filter(id => !savedSet.has(id) && !internalSet.has(id))

  return [...validSaved, ...newInternalColumns, ...newDataColumns]
}

function mergeColumnVisibility<TData extends MRT_RowData>(
  savedVisibility: Record<string, boolean> | undefined,
  columns: MRT_ColumnDef<TData>[],
): MRT_VisibilityState {
  if (!savedVisibility) return {}

  const dataColumnIds = new Set(getColumnIds(columns))
  const merged: MRT_VisibilityState = {}

  for (const [key, value] of Object.entries(savedVisibility)) {
    if (dataColumnIds.has(key)) {
      merged[key] = value
    }
  }

  return merged
}

export const useTomTableColumnPreferences = <TData extends MRT_RowData>(
  config?: ColumnPreferencesConfig<TData>,
): ColumnPreferencesResult | undefined => {
  const { columns, savedPreferences, onPreferencesChange } = config ?? {}

  const initializedWithSavedRef = useRef(savedPreferences !== undefined)

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns ? mergeColumnOrder(savedPreferences?.columnOrder, columns) : [],
  )
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() =>
    columns ? mergeColumnVisibility(savedPreferences?.columnVisibility, columns) : {},
  )

  useEffect(() => {
    if (initializedWithSavedRef.current || !columns || savedPreferences === undefined) return
    initializedWithSavedRef.current = true
    setColumnOrder(mergeColumnOrder(savedPreferences.columnOrder, columns))
    setColumnVisibility(mergeColumnVisibility(savedPreferences.columnVisibility, columns))
  }, [columns, savedPreferences])

  const columnOrderRef = useRef(columnOrder)
  columnOrderRef.current = columnOrder
  const columnVisibilityRef = useRef(columnVisibility)
  columnVisibilityRef.current = columnVisibility
  const onPreferencesChangeRef = useRef(onPreferencesChange)
  onPreferencesChangeRef.current = onPreferencesChange

  const savePreferences = useCallback(
    (visibility: MRT_VisibilityState, order: string[]) => {
      onPreferencesChangeRef.current?.({
        columnVisibility: visibility,
        columnOrder: order,
      })
    },
    [],
  )

  const onColumnVisibilityChange = useCallback(
    (updater: MRT_Updater<MRT_VisibilityState>) => {
      setColumnVisibility(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        savePreferences(next, columnOrderRef.current)
        return next
      })
    },
    [savePreferences],
  )

  const onColumnOrderChange = useCallback(
    (updater: MRT_Updater<string[]>) => {
      setColumnOrder(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        savePreferences(columnVisibilityRef.current, next)
        return next
      })
    },
    [savePreferences],
  )

  if (!config) return undefined

  return {
    columnVisibility,
    columnOrder,
    onColumnVisibilityChange,
    onColumnOrderChange,
  }
}
